import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Input, Space, Tag, Typography } from 'antd';
import { ArrowLeft, Search } from 'lucide-react';
import ContentBankRenderer from './ContentBankRenderer';
import { searchContentBank } from './contentBankService';
import {
  buildContentBankHighlightSnippet,
  getContentBankHighlightRanges,
  searchPublishedContentBankDocuments,
} from './contentBankUtils';
import type { ContentBankDocument, ContentBankSearchResult } from './types';

const { Paragraph, Text, Title } = Typography;

const styles: Record<string, React.CSSProperties> = {
  hero: {
    display: 'grid',
    gap: 18,
    marginBottom: 16,
  },
  search: {
    maxWidth: 680,
  },
  resultButton: {
    width: '100%',
    border: '1px solid #d6dee3',
    background: '#fff',
    borderRadius: 8,
    padding: 16,
    textAlign: 'left',
    cursor: 'pointer',
    display: 'grid',
    gap: 8,
  },
  resultButtonActive: {
    borderColor: '#0f766e',
    boxShadow: '0 0 0 2px rgba(15, 118, 110, 0.12)',
  },
  documentPanel: {
    background: '#fff',
    border: '1px solid #dce3e7',
    borderRadius: 8,
    padding: 24,
  },
};

type PublicFAQContentProps = {
  documents: ContentBankDocument[];
  isLoading: boolean;
  error: string | null;
  navigation?: React.ReactNode;
  onDocumentOpenChange?: (isOpen: boolean) => void;
};

function getPublishedDocument(document: ContentBankDocument) {
  return document.published?.status === 'published' ? document.published : null;
}

function createDocumentResult(document: ContentBankDocument): ContentBankSearchResult {
  const published = getPublishedDocument(document);
  const metadata = published?.metadata;

  return {
    id: document.id,
    title: published?.title || document.title,
    summary: metadata?.summary || (published?.plainText || '').slice(0, 160),
    snippet: metadata?.summary || (published?.plainText || '').slice(0, 180),
    categories: metadata?.categories || [],
    tags: metadata?.tags || [],
    score: 0,
    source: 'lexical',
  };
}

function getDocumentMatchingSnippet(document: ContentBankDocument, query: string) {
  const published = getPublishedDocument(document);
  if (!published) {
    return '';
  }

  return (
    buildContentBankHighlightSnippet(published.plainText || '', query, 260) ||
    buildContentBankHighlightSnippet(
      [
        published.metadata.summary,
        ...published.metadata.likelyQuestions,
        ...published.metadata.searchKeywords,
        ...published.metadata.tags,
        ...published.metadata.categories,
      ].join(' '),
      query,
      220
    )
  );
}

function mergeSearchResults(
  documents: ContentBankDocument[],
  backendResults: ContentBankSearchResult[],
  fallbackResults: ContentBankSearchResult[],
  query: string
) {
  const documentsById = new Map(documents.map((document) => [document.id, document]));
  const fallbackById = new Map(fallbackResults.map((result) => [result.id, result]));
  const combined = new Map<string, ContentBankSearchResult & { displayScore: number }>();

  function upsert(result: ContentBankSearchResult) {
    const document = documentsById.get(result.id);
    const fallback = fallbackById.get(result.id);
    const matchingSnippet = document ? getDocumentMatchingSnippet(document, query) : '';
    const hasTextMatch = Boolean(matchingSnippet || fallback);
    const current = combined.get(result.id);
    const score = (current?.displayScore || 0) + result.score + (fallback?.score || 0);
    const source =
      hasTextMatch && result.source === 'semantic'
        ? 'hybrid'
        : current?.source === 'semantic' && result.source === 'lexical'
        ? 'hybrid'
        : result.source;

    combined.set(result.id, {
      ...result,
      source,
      snippet: matchingSnippet || fallback?.snippet || result.snippet,
      score: Math.max(result.score, fallback?.score || 0, current?.score || 0),
      displayScore: score + (hasTextMatch ? 1000 : 0),
    });
  }

  backendResults.forEach(upsert);
  fallbackResults.forEach(upsert);

  return Array.from(combined.values())
    .sort((first, second) => second.displayScore - first.displayScore || first.title.localeCompare(second.title))
    .map(({ displayScore, ...result }) => result);
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  const ranges = useMemo(() => getContentBankHighlightRanges(text, query), [text, query]);

  if (!query.trim() || ranges.length === 0) {
    return <>{text}</>;
  }

  const parts: React.ReactNode[] = [];
  let cursor = 0;

  ranges.forEach((range, index) => {
    if (range.start > cursor) {
      parts.push(text.slice(cursor, range.start));
    }

    parts.push(
      <mark key={`${range.start}:${range.end}:${index}`} className="content-bank-search-highlight">
        {text.slice(range.start, range.end)}
      </mark>
    );
    cursor = range.end;
  });

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return <>{parts}</>;
}

function ResultCard({
  result,
  searchQuery,
  isActive = false,
  onSelect,
}: {
  result: ContentBankSearchResult;
  searchQuery: string;
  isActive?: boolean;
  onSelect: () => void;
}) {
  const excerpt = searchQuery.trim()
    ? result.snippet || result.summary
    : result.summary || result.snippet;

  return (
    <button
      type="button"
      style={{ ...styles.resultButton, ...(isActive ? styles.resultButtonActive : null) }}
      aria-pressed={isActive}
      onClick={onSelect}
    >
      <Space wrap size={4}>
        {result.categories.slice(0, 2).map((category) => (
          <Tag key={category}>{category}</Tag>
        ))}
        {result.source !== 'lexical' ? <Tag color="blue">Semantisk</Tag> : null}
      </Space>
      <Text strong style={{ color: '#0f172a' }}>
        <HighlightedText text={result.title} query={searchQuery} />
      </Text>
      <Text type="secondary">
        <HighlightedText text={excerpt} query={searchQuery} />
      </Text>
    </button>
  );
}

export default function PublicFAQContent({
  documents,
  isLoading,
  error,
  navigation,
  onDocumentOpenChange,
}: PublicFAQContentProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<ContentBankSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null);
  const activeDocument = documents.find((document) => document.id === activeDocumentId) || null;
  const activePublished = activeDocument ? getPublishedDocument(activeDocument) : null;

  const categories = useMemo(() => {
    const categoryMap = new Map<string, number>();
    documents.forEach((document) => {
      getPublishedDocument(document)?.metadata.categories.forEach((category) => {
        categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
      });
    });

    return Array.from(categoryMap.entries())
      .sort((first, second) => second[1] - first[1] || first[0].localeCompare(second[0]))
      .map(([category]) => category);
  }, [documents]);

  useEffect(() => {
    const query = searchTerm.trim();

    if (!query) {
      setResults([]);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    let isActive = true;
    setIsSearching(true);
    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await searchContentBank(query, 10);
        if (!isActive) {
          return;
        }

        const fallbackResults = searchPublishedContentBankDocuments(documents, query, 10);
        setResults(mergeSearchResults(documents, response.results, fallbackResults, query).slice(0, 10));
        setSearchError(response.fallbackReason || null);
      } catch (searchFailure) {
        if (!isActive) {
          return;
        }

        const fallbackResults = searchPublishedContentBankDocuments(documents, query, 10);
        setResults(mergeSearchResults(documents, [], fallbackResults, query).slice(0, 10));
        setSearchError(
          searchFailure instanceof Error ? searchFailure.message : 'Søgning kunne ikke gennemføres.'
        );
      } finally {
        if (isActive) {
          setIsSearching(false);
        }
      }
    }, 250);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [documents, searchTerm]);

  const hasSearchTerm = searchTerm.trim().length > 0;
  const visibleResults = hasSearchTerm
    ? results
    : documents.map(createDocumentResult).sort((first, second) => first.title.localeCompare(second.title));
  const hasVisibleResults = visibleResults.length > 0;
  const isDocumentOpen = Boolean(activePublished);

  useEffect(() => {
    onDocumentOpenChange?.(isDocumentOpen);
  }, [isDocumentOpen, onDocumentOpenChange]);

  const hero = (
    <section style={styles.hero}>
      {navigation}
      <div>
        <Title level={3} style={{ marginBottom: 8 }}>
          Hjælp og FAQ
        </Title>
        <Paragraph style={{ maxWidth: 760, fontSize: 16, marginBottom: 0 }}>
          Søg på tværs af alle publicerede hjælpedokumenter.
        </Paragraph>
        {isLoading ? <Text type="secondary">Indlæser opdateret indhold...</Text> : null}
      </div>
      <Input
        size="large"
        allowClear
        prefix={<Search size={18} />}
        placeholder="Søg efter fx lugt, vaskemaskine, sensor, slange..."
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        style={styles.search}
      />
    </section>
  );

  const statusMessages = (
    <>
      {error ? <Alert type="warning" message="FAQ kører på publiceret indhold" description={error} /> : null}
      {searchError ? (
        <Alert
          type="info"
          showIcon
          message="Semantisk søgning bruger fallback lige nu"
          description={searchError}
          style={{ marginBottom: 16 }}
        />
      ) : null}
    </>
  );

  const categoryButtons = !hasSearchTerm && !activePublished && categories.length > 0 ? (
    <Space wrap size={8} style={{ marginBottom: 20 }}>
      {categories.slice(0, 10).map((category) => (
        <Button key={category} onClick={() => setSearchTerm(category)}>
          {category}
        </Button>
      ))}
    </Space>
  ) : null;

  const resultsPane = hasVisibleResults ? (
    <section className="content-bank-faq-results-pane" aria-label="Søgeresultater">
      {activePublished ? (
        <Title level={5} style={{ marginTop: 0 }}>
          Resultater
        </Title>
      ) : null}
      <div className="content-bank-faq-result-list">
        {visibleResults.map((result) => (
          <ResultCard
            key={result.id}
            result={result}
            searchQuery={searchTerm}
            isActive={result.id === activeDocumentId}
            onSelect={() => setActiveDocumentId(result.id)}
          />
        ))}
      </div>
    </section>
  ) : isSearching ? null : (
    <Empty description="Ingen publicerede hjælpedokumenter matcher søgningen." />
  );

  const documentPane = activePublished ? (
    <section style={styles.documentPanel} className="content-bank-faq-document-pane">
      <div className="content-bank-faq-document-header">
        <Button
          icon={<ArrowLeft size={16} />}
          onClick={() => setActiveDocumentId(null)}
        >
          Luk dokument
        </Button>
        <Space wrap size={4}>
          {activePublished.metadata.categories.map((category) => (
            <Tag key={category}>{category}</Tag>
          ))}
        </Space>
        <div>
          <Title level={2} style={{ marginTop: 0 }}>
            <HighlightedText text={activePublished.title} query={searchTerm} />
          </Title>
          {activePublished.metadata.summary ? (
            <Paragraph style={{ marginBottom: 0 }}>
              <HighlightedText text={activePublished.metadata.summary} query={searchTerm} />
            </Paragraph>
          ) : null}
        </div>
      </div>
      <div className="content-bank-faq-document-scroll">
        <ContentBankRenderer
          value={activePublished.editorJson}
          contentKey={`${activeDocument?.id}:${activePublished.version}`}
          searchQuery={searchTerm}
        />
      </div>
    </section>
  ) : null;

  if (activePublished) {
    return (
      <div className="content-bank-faq-master-detail">
        <div className="content-bank-faq-master-column">
          {hero}
          {statusMessages}
          {isSearching ? <Text type="secondary">Søger...</Text> : null}
          {resultsPane}
        </div>
        {documentPane}
      </div>
    );
  }

  return (
    <>
      {hero}
      {statusMessages}
      {categoryButtons}
      {isSearching ? <Text type="secondary">Søger...</Text> : null}
      {resultsPane}
    </>
  );
}
